import { type IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/src/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { getIdFromParam, getSportKeyFromParam } from '@on3/ui-lib/utils/url';
import {
  type InterestPageResponse,
  InterestsPage,
} from 'components/Db/Profile/PlayerInterests/PlayerInterestsPage';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import type { GetServerSideProps } from 'next';
import { playerModules } from 'utils/player';

interface IPhotosInterestsPage
  extends InterestPageResponse,
    IGenericPageProps {}

const PlayerInterestsPage = ({
  teamTargets,
  basicInfo,
  ...props
}: IPhotosInterestsPage) => {
  const title = `${basicInfo?.name} Interests`;
  const noIndex = teamTargets?.pagination?.count === 0;

  return (
    <PageWrapper {...props} noIndex={noIndex} title={title}>
      <InterestsPage basicInfo={basicInfo} teamTargets={teamTargets} />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  const {
    id,
    sport,
    recruitingId,
    page = 1,
  } = ctx.query as {
    id: string;
    sport: string;
    recruitingId: string;
    page: string;
  };
  const playerId = getIdFromParam(id);

  if (!playerId) return { notFound: true };

  const personKey = +playerId;
  const sportKey =
    getSportKeyFromParam(sport?.toLowerCase() ?? '') ?? undefined;

  try {
    const [player] = await Promise.allSettled([
      playerModules.player(proxyApi, { personKey, sportKey }),
    ]);

    const fetchedPlayer = getFulfilledValue(player);

    // If player is not found, return 404
    if (!fetchedPlayer) return { notFound: true };

    if (id !== fetchedPlayer.slug) {
      return {
        redirect: {
          permanent: true,
          destination: `/db/${fetchedPlayer.slug}/recruiting/${recruitingId}/interests/`,
        },
      };
    }

    const params = {
      page: +page,
      sportKey,
      playerKey: personKey,
      recruitmentKey: +recruitingId,
    };
    const [teamTargets] = await Promise.allSettled([
      playerModules.topTeams(proxyApi, params),
    ]);

    const data = {
      pageType: 'database',
      siteData,
      userData: userData || {},
      basicInfo: fetchedPlayer,
      teamTargets: getFulfilledValue(teamTargets),
      page: getFulfilledValue(teamTargets),
    };

    return { props: { ...data } };
  } catch (e) {
    return { props: [] };
  }
};

export default withValidUrl(PlayerInterestsPage);
